/**
 * Ticket KL4: reine Logik des Kontakt-Imports.
 *
 * Bewusst ohne React, ohne Datenbank und ohne Dateizugriff: Trennzeichen,
 * Datumslesung, Spaltenvorschlag, Bündelung der Dubletten und die Prüfungen
 * sind damit vollständig testbar. Die Datei selbst wird im Browser gelesen
 * (siehe `contacts-import-file.ts`) und nie hochgeladen.
 */

import { BUNDESLAENDER, BUNDESLAND_LABELS, type Bundesland } from "@/lib/contacts";

/** Obergrenze je Vorgang – dieselbe Zahl prüft die Datenbankfunktion. */
export const IMPORT_MAX_CONTACTS = 2000;
/** Obergrenze der gelesenen Zeilen, damit der Browser nicht einfriert. */
export const IMPORT_MAX_ROWS = 5000;

export const IMPORT_FIELDS = [
  "email",
  "firstName",
  "lastName",
  "organization",
  "bundesland",
  "occurredAt",
  "source",
  "tag",
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number];

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  email: "E-Mail-Adresse",
  firstName: "Vorname",
  lastName: "Nachname",
  organization: "Organisation",
  bundesland: "Bundesland",
  occurredAt: "Zeitpunkt",
  source: "Herkunft",
  tag: "Schlagwort",
};

export const IMPORT_FIELD_HINTS: Record<ImportField, string> = {
  email: "Pflichtangabe. Zeilen ohne gültige Adresse werden übersprungen.",
  firstName: "",
  lastName: "",
  organization: "",
  bundesland: "Werte werden im nächsten Schritt einem Bundesland zugeordnet.",
  occurredAt: "Format Tag.Monat.Jahr, auch zweistelliges Jahr. Ohne Angabe gilt der Importzeitpunkt.",
  source: "Werte werden im nächsten Schritt einer geführten Herkunft zugeordnet.",
  tag: "Mehrere Schlagworte je Zelle sind möglich, getrennt durch Komma, Semikolon, Schrägstrich oder senkrechten Strich.",
};

/** Zuordnung Zielfeld -> Spaltenname der Datei (null = nicht zugeordnet). */
export type ColumnMap = Record<ImportField, string | null>;

export const EMPTY_COLUMN_MAP: ColumnMap = {
  email: null,
  firstName: null,
  lastName: null,
  organization: null,
  bundesland: null,
  occurredAt: null,
  source: null,
  tag: null,
};


export type ImportSheet = { headers: string[]; rows: Record<string, string>[] };

/* ------------------------------------------------------------------ CSV */

/** Häufigstes Trennzeichen der Kopfzeile gewinnt; Standard ist Semikolon. */
export function detectDelimiter(text: string): ";" | "," | "\t" {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const counts: Array<[";" | "," | "\t", number]> = [
    [";", (firstLine.match(/;/gu) ?? []).length],
    [",", (firstLine.match(/,/gu) ?? []).length],
    ["\t", (firstLine.match(/\t/gu) ?? []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ";";
}

/** Kleiner Leser mit Anführungszeichen-Behandlung; Werte bleiben Text. */
export function parseCsv(text: string, delimiter?: string): ImportSheet {
  const clean = text.replace(/^\uFEFF/u, "");
  const sep = delimiter ?? detectDelimiter(clean);
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;

  for (let i = 0; i < clean.length; i += 1) {
    const ch = clean[i];
    if (quoted) {
      if (ch === '"') {
        if (clean[i + 1] === '"') {
          value += '"';
          i += 1;
        } else quoted = false;
      } else value += ch;
      continue;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === sep) {
      row.push(value);
      value = "";
    } else if (ch === "\n") {
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
    } else if (ch !== "\r") {
      value += ch;
    }
  }
  if (value !== "" || row.length > 0) {
    row.push(value);
    rows.push(row);
  }

  return toSheet(rows);
}

/** Aus einer Wertematrix (CSV oder Excel) wird Kopfzeile plus Datenzeilen. */
export function toSheet(matrix: string[][]): ImportSheet {
  const nonEmpty = matrix.filter((r) => r.some((c) => (c ?? "").trim() !== ""));
  if (nonEmpty.length === 0) return { headers: [], rows: [] };
  const rawHeaders = (nonEmpty[0] ?? []).map((h) => (h ?? "").trim());
  const headers: string[] = [];
  rawHeaders.forEach((h, index) => {
    const base = h === "" ? `Spalte ${index + 1}` : h;
    let name = base;
    let n = 2;
    while (headers.includes(name)) {
      name = `${base} (${n})`;
      n += 1;
    }
    headers.push(name);
  });

  const rows = nonEmpty.slice(1, IMPORT_MAX_ROWS + 1).map((cells) => {
    const record: Record<string, string> = {};
    headers.forEach((h, index) => {
      record[h] = (cells[index] ?? "").trim();
    });
    return record;
  });

  return { headers, rows };
}

/* -------------------------------------------------------------- Vorschlag */

const FIELD_PATTERNS: Record<ImportField, RegExp[]> = {
  email: [/^e-?\s?mail/iu, /mail(adresse)?$/iu],
  firstName: [/^vor\s?name/iu, /^first/iu],
  lastName: [/^nach\s?name/iu, /^last/iu, /^sur/iu],
  organization: [/organisation/iu, /firma/iu, /unternehmen/iu, /company/iu],
  bundesland: [/bundesland/iu, /^land$/iu, /^herkunft$/iu, /^region/iu, /^state/iu],
  occurredAt: [/aufnahme/iu, /datum/iu, /zeitpunkt/iu, /date/iu, /^angemeldet/iu],
  source: [/^input$/iu, /^quelle/iu, /^herkunft/iu, /^source/iu, /freebie/iu],
  tag: [/^tags?$/iu, /^schlagwort/iu, /^schlagworte$/iu, /^label/iu, /^kategorie/iu],
};

/**
 * Vorschlag anhand der Spaltennamen. Die Reihenfolge der Felder entscheidet:
 * „Herkunft" passt sowohl auf das Bundesland als auch auf die Quelle, deshalb
 * gewinnt „Input" für die Quelle und „Herkunft" bleibt beim Bundesland.
 */
export function suggestColumnMap(headers: string[]): ColumnMap {
  const map: ColumnMap = { ...EMPTY_COLUMN_MAP };
  const taken = new Set<string>();
  const order: ImportField[] = [
    "email",
    "firstName",
    "lastName",
    "organization",
    "occurredAt",
    "source",
    "tag",
    "bundesland",
  ];

  for (const field of order) {
    for (const pattern of FIELD_PATTERNS[field]) {
      const hit = headers.find((h) => !taken.has(h) && pattern.test(h));
      if (hit) {
        map[field] = hit;
        taken.add(hit);
        break;
      }
    }
  }
  return map;
}

/* ----------------------------------------------------------------- Datum */

/**
 * Liest `TT.MM.JJ`, `TT.MM.JJJJ`, `JJJJ-MM-TT` sowie Excel-Tageszahlen.
 * Zweistellige Jahre gelten als 20xx – der Bestand reicht von 2023 bis 2026.
 * Rückgabe ist ein ISO-Zeitpunkt zur Mittagsstunde, damit keine Zeitzone das
 * Datum um einen Tag verschiebt.
 */
export function parseImportDate(raw: string): string | null {
  const value = (raw ?? "").trim();
  if (value === "") return null;

  const dotted = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2}|\d{4})$/u.exec(value);
  if (dotted) {
    const day = Number(dotted[1]);
    const month = Number(dotted[2]);
    let year = Number(dotted[3]);
    if (dotted[3].length === 2) year += year <= 79 ? 2000 : 1900;
    return buildIso(year, month, day);
  }

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/u.exec(value);
  if (iso) return buildIso(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // Excel-Tageszahl (Bezug 30.12.1899) – nur als Rückfalllösung.
  if (/^\d{5}$/u.test(value)) {
    const ms = (Number(value) - 25569) * 86400000;
    const d = new Date(ms);
    return buildIso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }

  return null;
}

function buildIso(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (year < 1990 || year > 2100) return null;
  const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  if (d.getUTCMonth() + 1 !== month || d.getUTCDate() !== day) return null;
  return d.toISOString();
}

/* ------------------------------------------------------------- Adressen */

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/iu;

export function isImportableEmail(raw: string): boolean {
  return EMAIL_RE.test((raw ?? "").trim());
}

export function normalizeImportEmail(raw: string): string {
  return (raw ?? "").trim().toLowerCase();
}

/* ------------------------------------------------- Werteübersetzung */

/**
 * Ergänzung zu Ticket KL4: Je Wert der Datei gibt es drei Möglichkeiten –
 * einem vorhandenen Eintrag zuordnen, ausdrücklich einen neuen anlegen, oder
 * weglassen (kein Eintrag in der Zuordnung). Nichts entsteht stillschweigend.
 */
export type ValueTarget = { kind: "existing"; id: string } | { kind: "new"; name: string };

/** Wert aus der Datei -> Ziel (Herkunft, Schlagwort) bzw. Bundesland-Schlüssel. */
export type ValueMap = {
  source: Record<string, ValueTarget>;
  bundesland: Record<string, string>;
  tag: Record<string, ValueTarget>;
};

export const EMPTY_VALUE_MAP: ValueMap = { source: {}, bundesland: {}, tag: {} };

/** Trennzeichen einer Schlagwort-Zelle. Ohne Trennzeichen bleibt es ein Wert. */
export const TAG_CELL_SEPARATORS = /[,;/|]/u;

/**
 * Eine Zelle kann mehrere Schlagworte tragen. Jeder Teil wird um Leerzeichen
 * bereinigt; Dubletten innerhalb einer Zelle zählen einmal. Enthält die Zelle
 * kein Trennzeichen, ergibt sich genau ein Wert – der Normalfall bleibt also
 * ohne Sonderbehandlung.
 */
export function splitTagCell(raw: string): string[] {
  const parts = (raw ?? "").split(TAG_CELL_SEPARATORS);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const value = part.replace(/\s+/gu, " ").trim();
    if (value === "") continue;
    const key = value.toLocaleLowerCase("de-DE");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/** Vergleichsform eines Wertes: ohne Randleerzeichen, ohne Groß-/Kleinschreibung. */
export function normalizeImportValue(raw: string): string {
  return (raw ?? "").replace(/\s+/gu, " ").trim().toLocaleLowerCase("de-DE");
}

/** Platzhalter für einen erst beim Übernehmen entstehenden Eintrag. */
export function newEntryRef(name: string): string {
  return `#${normalizeImportValue(name)}`;
}

export type ValueTally = { value: string; count: number };

/** Alle vorkommenden Werte einer Spalte mit ihrer Anzahl, häufigste zuerst. */
export function tallyColumn(rows: Record<string, string>[], column: string | null): ValueTally[] {
  if (!column) return [];
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = (row[column] ?? "").trim();
    if (value === "") continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, "de-DE"));
}

/** Wie `tallyColumn`, aber für Zellen mit mehreren Werten (Schlagworte). */
export function tallyTagColumn(
  rows: Record<string, string>[],
  column: string | null,
): ValueTally[] {
  if (!column) return [];
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const value of splitTagCell(row[column] ?? "")) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, "de-DE"));
}


/** Vorschlag für Bundesländer anhand der Bezeichnung („Baden-Württemberg"). */
export function suggestBundesland(value: string): Bundesland | null {
  const needle = value.trim().toLocaleLowerCase("de-DE").replace(/[\s_-]+/gu, "");
  for (const key of BUNDESLAENDER) {
    if (needle === key.replace(/_/gu, "")) return key;
    if (needle === BUNDESLAND_LABELS[key].toLocaleLowerCase("de-DE").replace(/[\s_-]+/gu, "")) {
      return key;
    }
  }
  return null;
}

/* ----------------------------------------------------------- Bündelung */

export type PlannedOrigin = {
  sourceId: string;
  occurredAt: string | null;
};

export type PlannedContact = {
  email: string;
  firstName: string;
  lastName: string;
  organization: string | null;
  bundesland: Bundesland | null;
  origins: PlannedOrigin[];
  /** Kennungen vorhandener Schlagworte oder Platzhalter neuer („#wert"). */
  tags: string[];
  /** Zeilennummern der Datei, aus denen dieser Kontakt entstand. */
  rowNumbers: number[];
};

export type SkippedRow = { rowNumber: number; email: string; reason: string };

export type ImportConflict = {
  email: string;
  field: "Nachname" | "Vorname" | "Organisation" | "Bundesland";
  kept: string;
  other: string;
  rowNumbers: number[];
};

/** Ein Wert der Datei, der ohne Zuordnung bleibt und deshalb wegfällt. */
export type UnmappedValue = { value: string; rows: number };

/** Neu anzulegender Eintrag samt Platzhalter, auf den die Zeilen verweisen. */
export type NewEntry = { key: string; name: string; rows: number };

export type ImportPlan = {
  contacts: PlannedContact[];
  skipped: SkippedRow[];
  conflicts: ImportConflict[];
  newSources: NewEntry[];
  newTags: NewEntry[];
  stats: {
    rowsRead: number;
    contactsTotal: number;
    contactsWithSeveralOrigins: number;
    contactsWithBundesland: number;
    contactsWithTags: number;
    multiRowEmails: number;
    unreadableDates: number;
    unmappedSourceValues: number;
    unmappedBundeslandValues: number;
    unmappedTagValues: number;
    missingSource: number;
    missingTag: number;
    unmappedSource: UnmappedValue[];
    unmappedBundesland: UnmappedValue[];
    unmappedTag: UnmappedValue[];
    /** Zeilen, in denen Herkunft und Schlagwort aus demselben Wert entstehen. */
    sameValueRows: number;
    sameValues: UnmappedValue[];
    perSource: Record<string, number>;
    perTag: Record<string, number>;
  };
};

const FIELD_LABEL: Record<"firstName" | "lastName" | "organization" | "bundesland", ImportConflict["field"]> = {
  firstName: "Vorname",
  lastName: "Nachname",
  organization: "Organisation",
  bundesland: "Bundesland",
};

function bump(map: Map<string, number>, key: string): void {
  map.set(key, (map.get(key) ?? 0) + 1);
}

function toList(map: Map<string, number>): UnmappedValue[] {
  return [...map.entries()]
    .map(([value, rows]) => ({ value, rows }))
    .sort((a, b) => b.rows - a.rows || a.value.localeCompare(b.value, "de-DE"));
}

/**
 * Punkt 3 des Tickets: Mehrere Zeilen mit derselben Adresse ergeben einen
 * Kontakt. Stammdaten kommen aus der ersten Zeile, jede weitere Zeile liefert
 * nur eine zusätzliche Herkunft. Dieselbe Herkunft zweimal ergibt eine
 * Herkunft mit dem frühesten Zeitpunkt (Regel aus Ticket KL3). Schlagworte
 * sammeln sich über alle Zeilen einer Adresse, jedes höchstens einmal.
 */
export function buildImportPlan(input: {
  rows: Record<string, string>[];
  columnMap: ColumnMap;
  valueMap: ValueMap;
}): ImportPlan {
  const { rows, columnMap, valueMap } = input;
  const byEmail = new Map<string, PlannedContact>();
  const originTimes = new Map<string, Map<string, string | null>>();
  const tagRefs = new Map<string, Set<string>>();
  const skipped: SkippedRow[] = [];
  const conflicts: ImportConflict[] = [];
  let unreadableDates = 0;
  let unmappedSourceValues = 0;
  let unmappedBundeslandValues = 0;
  let unmappedTagValues = 0;
  let missingSource = 0;
  let missingTag = 0;
  let sameValueRows = 0;
  const unmappedSource = new Map<string, number>();
  const unmappedBundesland = new Map<string, number>();
  const unmappedTag = new Map<string, number>();
  const sameValues = new Map<string, number>();
  const newSourceRows = new Map<string, { name: string; rows: number }>();
  const newTagRows = new Map<string, { name: string; rows: number }>();

  const cell = (row: Record<string, string>, field: ImportField): string => {
    const column = columnMap[field];
    return column ? (row[column] ?? "").trim() : "";
  };

  /** Ziel eines Wertes auflösen: Kennung, Platzhalter oder null (weglassen). */
  const resolve = (
    target: ValueTarget | undefined,
    bucket: Map<string, { name: string; rows: number }>,
  ): string | null => {
    if (!target) return null;
    if (target.kind === "existing") return target.id;
    const name = target.name.replace(/\s+/gu, " ").trim();
    if (name === "") return null;
    const key = normalizeImportValue(name);
    const known = bucket.get(key);
    if (known) known.rows += 1;
    else bucket.set(key, { name, rows: 1 });
    return `#${key}`;
  };

  rows.forEach((row, index) => {
    const rowNumber = index + 2; // Kopfzeile ist Zeile 1
    const rawEmail = cell(row, "email");
    if (rawEmail === "") {
      skipped.push({ rowNumber, email: "", reason: "Keine E-Mail-Adresse in der Zeile" });
      return;
    }
    if (!isImportableEmail(rawEmail)) {
      skipped.push({ rowNumber, email: rawEmail, reason: "E-Mail-Adresse ist ungültig" });
      return;
    }
    const email = normalizeImportEmail(rawEmail);

    const rawSource = cell(row, "source");
    const sourceId = rawSource === "" ? null : resolve(valueMap.source[rawSource], newSourceRows);
    if (rawSource !== "" && !sourceId) {
      unmappedSourceValues += 1;
      bump(unmappedSource, rawSource);
    }
    if (rawSource === "") missingSource += 1;

    const rawTagCell = cell(row, "tag");
    const rawTags = splitTagCell(rawTagCell);
    if (columnMap.tag && rawTags.length === 0) missingTag += 1;
    const rowTagRefs: string[] = [];
    for (const value of rawTags) {
      const ref = resolve(valueMap.tag[value], newTagRows);
      if (!ref) {
        unmappedTagValues += 1;
        bump(unmappedTag, value);
        continue;
      }
      rowTagRefs.push(ref);
      // Hinweis: Herkunft und Schlagwort entstehen aus demselben Wert.
      if (rawSource !== "" && normalizeImportValue(value) === normalizeImportValue(rawSource)) {
        sameValueRows += 1;
        bump(sameValues, rawSource);
      }
    }

    const rawDate = cell(row, "occurredAt");
    const occurredAt = rawDate === "" ? null : parseImportDate(rawDate);
    if (rawDate !== "" && occurredAt === null) unreadableDates += 1;

    const rawLand = cell(row, "bundesland");
    const landKey = rawLand === "" ? null : (valueMap.bundesland[rawLand] ?? null);
    if (rawLand !== "" && !landKey) {
      unmappedBundeslandValues += 1;
      bump(unmappedBundesland, rawLand);
    }
    const bundesland = (landKey && (BUNDESLAENDER as string[]).includes(landKey)
      ? (landKey as Bundesland)
      : null);

    let entry = byEmail.get(email);
    if (!entry) {
      entry = {
        email,
        firstName: cell(row, "firstName"),
        lastName: cell(row, "lastName"),
        organization: cell(row, "organization") || null,
        bundesland,
        origins: [],
        tags: [],
        rowNumbers: [rowNumber],
      };
      byEmail.set(email, entry);
      originTimes.set(email, new Map());
      tagRefs.set(email, new Set());
    } else {
      entry.rowNumbers.push(rowNumber);
      // Widersprüche: erster Wert bleibt, beide Werte kommen in den Bericht.
      const compare: Array<[keyof typeof FIELD_LABEL, string, string]> = [
        ["firstName", entry.firstName, cell(row, "firstName")],
        ["lastName", entry.lastName, cell(row, "lastName")],
        ["organization", entry.organization ?? "", cell(row, "organization")],
        ["bundesland", entry.bundesland ?? "", bundesland ?? ""],
      ];
      for (const [key, kept, other] of compare) {
        if (other === "" || kept === "" || kept === other) {
          // Leere Angaben ergänzen den Kontakt, ohne Widerspruch zu sein.
          if (kept === "" && other !== "") {
            if (key === "organization") entry.organization = other;
            else if (key === "bundesland") entry.bundesland = other as Bundesland;
            else entry[key] = other;
          }
          continue;
        }
        const existing = conflicts.find(
          (c) => c.email === email && c.field === FIELD_LABEL[key] && c.other === other,
        );
        if (existing) existing.rowNumbers.push(rowNumber);
        else {
          conflicts.push({
            email,
            field: FIELD_LABEL[key],
            kept,
            other,
            rowNumbers: [rowNumber],
          });
        }
      }
    }

    if (sourceId) {
      const times = originTimes.get(email)!;
      const known = times.get(sourceId);
      if (known === undefined) times.set(sourceId, occurredAt);
      else if (occurredAt && (known === null || occurredAt < known)) times.set(sourceId, occurredAt);
    }

    const refs = tagRefs.get(email)!;
    for (const ref of rowTagRefs) refs.add(ref);
  });

  const perSource: Record<string, number> = {};
  const perTag: Record<string, number> = {};
  const contacts = [...byEmail.values()].map((entry) => {
    const times = originTimes.get(entry.email)!;
    const origins = [...times.entries()].map(([sourceId, occurredAt]) => {
      perSource[sourceId] = (perSource[sourceId] ?? 0) + 1;
      return { sourceId, occurredAt };
    });
    const tags = [...tagRefs.get(entry.email)!];
    for (const ref of tags) perTag[ref] = (perTag[ref] ?? 0) + 1;
    return { ...entry, origins, tags };
  });

  return {
    contacts,
    skipped,
    conflicts,
    newSources: [...newSourceRows.entries()].map(([key, v]) => ({ key, name: v.name, rows: v.rows })),
    newTags: [...newTagRows.entries()].map(([key, v]) => ({ key, name: v.name, rows: v.rows })),
    stats: {
      rowsRead: rows.length,
      contactsTotal: contacts.length,
      contactsWithSeveralOrigins: contacts.filter((c) => c.origins.length > 1).length,
      contactsWithBundesland: contacts.filter((c) => c.bundesland !== null).length,
      contactsWithTags: contacts.filter((c) => c.tags.length > 0).length,
      multiRowEmails: contacts.filter((c) => c.rowNumbers.length > 1).length,
      unreadableDates,
      unmappedSourceValues,
      unmappedBundeslandValues,
      unmappedTagValues,
      missingSource,
      missingTag,
      unmappedSource: toList(unmappedSource),
      unmappedBundesland: toList(unmappedBundesland),
      unmappedTag: toList(unmappedTag),
      sameValueRows,
      sameValues: toList(sameValues),
      perSource,
      perTag,
    },
  };

}
